'use client'

import {createCache, extractStyle, StyleProvider} from '@ant-design/cssinjs'
import {useServerInsertedHTML} from 'next/navigation'
import React, {useEffect, useState} from 'react'
import {ServerStyleSheet, StyleSheetManager} from 'styled-components'
import {ResetStyles} from '@/src/shared/assets/styles/reset'
import {LAYER_STATEMENT} from '@/src/shared/assets/styles/layers'

export default function StyleRegistry({children}: {children: React.ReactNode}) {
  const [antdCache] = useState(() => createCache())
  const [styledComponentsStyleSheet] = useState(() => new ServerStyleSheet())

  useServerInsertedHTML(() => {
    const antdStyles = extractStyle(antdCache, {
      plain: true,
      once: true,
    })

    const styledComponentsStyles = styledComponentsStyleSheet.getStyleElement()
    styledComponentsStyleSheet.instance.clearTag()

    return (
      <>
        <style data-layer-statement>{LAYER_STATEMENT}</style>
        {antdStyles.includes('.data-ant-cssinjs-cache-path{content:"";}') ? (
          styledComponentsStyles
        ) : (
          <>
            <style dangerouslySetInnerHTML={{__html: ResetStyles.toString()}} />
            <style
              id="antd-cssinjs"
              data-rc-order="prepend"
              data-rc-priority="-1000"
              dangerouslySetInnerHTML={{__html: antdStyles}}
            />
            {styledComponentsStyles}
          </>
        )}
      </>
    )
  })

  useEffect(() => {
    const pinLayerStatement = () => {
      const statements = document.querySelectorAll<HTMLStyleElement>('[data-layer-statement]')
      statements.forEach((node, index) => {
        if (index > 0) node.remove()
      })

      const statement = statements[0] ?? document.createElement('style')

      if (statement.parentNode === null) {
        statement.setAttribute('data-layer-statement', '')
        statement.textContent = LAYER_STATEMENT
        document.head.insertBefore(statement, document.head.firstChild)
        return
      }

      if (document.head.firstChild !== statement) {
        document.head.insertBefore(statement, document.head.firstChild)
      }
    }

    pinLayerStatement()

    const observer = new MutationObserver(pinLayerStatement)
    observer.observe(document.head, {childList: true})
    return () => observer.disconnect()
  }, [])

  return (
    <StyleProvider cache={antdCache} layer>
      {typeof window !== 'undefined' ? (
        children
      ) : (
        <StyleSheetManager sheet={styledComponentsStyleSheet.instance}>
          {children}
        </StyleSheetManager>
      )}
    </StyleProvider>
  )
}
